import { useDispatch, useSelector } from "react-redux";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import {
  CirclePlusIcon,
  InformationIcon,
  MakerIcon,
  WarningIcon
} from "@/assets/icons";
import EmptyPlaceholder from "@/components/emptyPlaceholder";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MAX_POI } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setIsShowMarkerLabel } from "@/store/slice/spatialSlice";
import { toggleDatasetExplorerPopup } from "@/store/slice/uiSlice";
import { CatchmentDatasetConfig } from "@/types/spatial";

import CatchmentDatasetList from "./catchmentDatasetList";

interface Props {
  onOpenDatasetTabularModal?: () => void;
}
export function countTotalRows(datasets?: CatchmentDatasetConfig["datasets"]) {
  if (!datasets) return 0;

  let totalRows = 0;

  for (const key of Object.keys(datasets)) {
    const rows = datasets[key]?.rawDataset?.rows;
    if (Array.isArray(rows)) {
      totalRows += rows.length; // Intentionally summing all rows.length across datasets, not just assigning one value
    }
  }

  return totalRows;
}

const CatchmentDataset = ({ onOpenDatasetTabularModal }: Props) => {
  const dispatch = useDispatch();
  const { hideAllMarkerLabels, showAllMarkerLabels } = useMap();
  const { catchmentSettingsTemp, isShowMarkerLabel } = useSelector(
    (state: RootState) => state.spatial
  );
  const { data } = useGetSettings();
  const MAX_CATCHMENTS = data?.max_catchments || MAX_POI;
  const { isNewUIDatasetConfigEnabled, isBVTDataForCatchmentEnabled } =
    useAppFeatures();

  const totalCatchmentRows = countTotalRows(
    catchmentSettingsTemp?.datasetConfig?.datasets
  );

  const isCatchmentOverLimit = totalCatchmentRows > MAX_CATCHMENTS;

  const handleChangeShowLabel = (checked: boolean, datasetId?: string) => {
    dispatch(setIsShowMarkerLabel({ checked, datasetId }));
    if (checked) {
      showAllMarkerLabels();
    } else {
      hideAllMarkerLabels();
    }
  };

  const canAddMoreDatasets = totalCatchmentRows < MAX_CATCHMENTS;
  const hasNoDatasets =
    catchmentSettingsTemp?.datasetConfig?.visualizationOrder.length === 0;

  const firstCatchmentDatasetId =
    catchmentSettingsTemp?.datasetConfig?.visualizationOrder?.[0];
  const firstCatchmentAggType = firstCatchmentDatasetId
    ? catchmentSettingsTemp?.datasetConfig?.datasets?.[firstCatchmentDatasetId]
        ?.dataset?.data?.spatial_aggregation_type
    : undefined;
  const getCatchmentListLabel = () => {
    if (firstCatchmentAggType === "polygon") return "List of polygon";
    if (firstCatchmentAggType === "poi") return "List of marker";
    return "List of catchments";
  };
  const catchmentListLabel = getCatchmentListLabel();

  const informationLabel =
    firstCatchmentAggType === "polygon" ? "Polygon" : "POI";
  return (
    <div className="flex flex-col">
      {isBVTDataForCatchmentEnabled && (
        <p className="text-[10px] mt-2">
          {catchmentListLabel}{" "}
          <span className="font-bold text-teal-500">
            {totalCatchmentRows}/{MAX_CATCHMENTS}
          </span>
        </p>
      )}
      {isBVTDataForCatchmentEnabled && isCatchmentOverLimit && (
        <div
          className="flex items-start gap-2"
          role="alert"
          aria-label="Catchment reach limit warning"
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-red-50">
            <WarningIcon
              className="fill-cinnabar size-4"
              data-testid="warning-icon"
            />
          </div>

          <div className="flex flex-col">
            <div className="text-cinnabar leading-6 font-bold">Reach Limit</div>

            <span className="text-[10px] text-neutral-500">
              Your data has {totalCatchmentRows} rows and exceeds the maximum
              limit. Please filter the attributes first before saving the
              catchment.
            </span>
          </div>
        </div>
      )}
      {!isBVTDataForCatchmentEnabled && hasNoDatasets && (
        <Button
          variant="outline"
          className="rounded-xl h-[32px] font-bold btn-secondary mt-2"
          onClick={() => {
            dispatch(toggleDatasetExplorerPopup(true));
          }}
        >
          <CirclePlusIcon />
          <span>Add Dataset</span>
        </Button>
      )}
      <CatchmentDatasetList
        onOpenDatasetTabularModal={onOpenDatasetTabularModal}
        handleChangeShowLabel={handleChangeShowLabel}
      />
      <div className="flex flex-row items-center mt-2">
        {!isBVTDataForCatchmentEnabled && (
          <p className="text-[10px] mt-2">
            List of marker{" "}
            <span className="text-teal-500 font-bold">
              {countTotalRows(catchmentSettingsTemp?.datasetConfig?.datasets)}/
              {MAX_POI}
            </span>
          </p>
        )}
        {!isNewUIDatasetConfigEnabled && (
          <div className="flex flex-row items-center ml-auto">
            <span className="text-[10px] text-txtHeading mr-2">Show label</span>
            <Switch
              checked={isShowMarkerLabel}
              onCheckedChange={(checked) => handleChangeShowLabel(checked)}
            />
          </div>
        )}
      </div>
      {isBVTDataForCatchmentEnabled &&
        firstCatchmentDatasetId &&
        !isCatchmentOverLimit && (
          <div
            className="flex items-start gap-2"
            aria-label="Catchment dataset type lock information"
            role="note"
          >
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-50">
              <InformationIcon
                className="fill-blue-500 size-4"
                data-testid="information-icon"
              />
            </div>

            <div className="flex flex-col">
              <div className="text-blue-500 leading-6 font-bold">
                Information
              </div>

              <span className="text-[10px] text-neutral-500">
                You already have a {informationLabel} dataset added. Only same
                dataset type can be added to this catchment.
              </span>
            </div>
          </div>
        )}
      {isBVTDataForCatchmentEnabled && (
        <Button
          variant="outline"
          className="rounded-xl h-[32px] font-bold btn-secondary mt-2"
          onClick={() => {
            dispatch(toggleDatasetExplorerPopup(true));
          }}
          disabled={!canAddMoreDatasets}
        >
          <CirclePlusIcon />
          <span>Add Dataset</span>
        </Button>
      )}
      {hasNoDatasets && (
        <EmptyPlaceholder
          className="my-2 bg-white px-4"
          icon={
            <MakerIcon className="fill-light-gray" width={21} height={26} />
          }
          description="Add a catchment or upload a dataset. Only one method can be used at a time."
        />
      )}
    </div>
  );
};

export default CatchmentDataset;
